/**
 * Inquiry - 1:1 문의 모델
 * 
 * 사용자가 운영회사에 문의하는 내용을 저장합니다.
 * 슈퍼어드민이 문의를 확인하고 영업 활동에 활용합니다.
 * 
 * Firestore Path:
 * - 전역: inquiries/{inquiryId} (슈퍼어드민 접근용)
 * - 워크스페이스별: workspaces/{wsId}/inquiries/{inquiryId} (사용자 접근용)
 */

import { WorkspaceTier } from './local/Types';

// 문의 유형
export type InquiryType = 'pricing' | 'support' | 'contract' | 'other';

// 문의 상태
export type InquiryStatus = 'new' | 'in_progress' | 'resolved';

// 우선순위
export type InquiryPriority = 'low' | 'medium' | 'high';

// 문의 시점의 워크스페이스 스냅샷
export interface InquirySnapshot {
    tier: WorkspaceTier;
    memberCount: number;
    storageBytes: number;
    channelCount: number;
}

// 쓰레드 메시지 (고객 ↔ 슈퍼어드민 대화)
export interface InquiryMessage {
    id: string;
    content: string;
    authorId: string;
    authorName: string;
    authorRole: 'customer' | 'admin';    // customer: 고객, admin: 슈퍼어드민
    createdAt: Date;
}

export interface Inquiry {
    id: string;
    workspaceId: string;
    workspaceName: string;

    // 문의 내용
    type: InquiryType;
    subject: string;
    message: string;

    // 워크스페이스 스냅샷 (문의 시점 정보)
    snapshot: InquirySnapshot;

    // 쓰레드 대화
    messages?: InquiryMessage[];

    // 관리 상태
    status: InquiryStatus;
    priority: InquiryPriority;
    assignedTo?: string;           // 담당자 UID
    resolution?: string;           // 고객에게 표시되는 답변 (deprecated - messages 사용)
    internalNote?: string;         // 내부 메모 (슈퍼어드민만)

    // 감사
    createdBy: string;             // 문의자 UID
    createdAt: Date;
    updatedAt: Date;
}

// 컬렉션 경로
export const INQUIRIES_COL = 'inquiries';

// 기본값
export const defaultInquiry = (workspaceId: string, workspaceName: string): Partial<Inquiry> => ({
    workspaceId,
    workspaceName,
    type: 'support',
    subject: '',
    message: '',
    status: 'new',
    priority: 'medium',
    createdAt: new Date(),
    updatedAt: new Date(),
});

import { msg } from '@lingui/macro';
import { MessageDescriptor } from '@lingui/core';

// 문의 유형 레이블 (번역 가능)
export const INQUIRY_TYPE_LABELS: Record<InquiryType, MessageDescriptor> = {
    pricing: msg`Pricing & Plans`,
    support: msg`Feature & Technical Support`,
    contract: msg`Contract Inquiry`,
    other: msg`Other`,
};

// 문의 상태 레이블 (번역 가능)
export const INQUIRY_STATUS_LABELS: Record<InquiryStatus, MessageDescriptor> = {
    new: msg`New`,
    in_progress: msg`In Progress`,
    resolved: msg`Resolved`,
};
